import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Modal, Form, Row, Col, InputGroup } from "react-bootstrap";
import CompanySelectComponent from "./CompanySelectComponent";
import AddCompanyComponent from "./AddCompanyComponent";
import { addCustomer } from "state/customer/actions";
import { initialize } from "state/adminData/actions";
import {
  setCustomer,
  setCompany,
  setBillingInfo,
} from "state/orderEdition/actions";
import Select from "react-select";
import swal from "sweetalert";
import { ATSCustomer } from "transformer/customer/customerTransformer";
import AddressAutocompleteInput from "./AddressAutocompleteInput";

const AddCustomerComponent = ({ onCreated = null }) => {
  const dispatch = useDispatch();
  const [show, setShow] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const billingInfo = useSelector((state) => state.orderEdition.billingInfo);

  const companies = useSelector((state) =>
    state.adminData.companies ? state.adminData.companies : []
  );

  const addresses = useSelector((state) =>
    state.adminData.addresses ? state.adminData.addresses : []
  );

  const [optionSelected, setSelectedOptions] = useState(null);

  const formatAddressPreview = useCallback((address) => {
    if (!address) {
      return "";
    }
    return [
      address.street_number,
      address.street,
      address.postal_code,
      address.city,
    ]
      .filter(Boolean)
      .join(" ")
      .replace(/\s+/g, " ")
      .trim();
  }, []);

  const buildAddressOption = useCallback(
    (address) => {
      const preview = formatAddressPreview(address);
      return {
        value: address.id,
        label: [address.name_address, preview].filter(Boolean).join(" - "),
        preview,
        ...address,
      };
    },
    [formatAddressPreview]
  );

  const filterAddressOption = (option, rawInput) => {
    if (!rawInput) {
      return true;
    }
    const input = rawInput.toLowerCase();
    const data = option.data || option;
    return [
      option.label,
      data.name_address,
      data.preview,
      data.street,
      data.street_number,
      data.postal_code,
      data.city,
    ]
      .filter(Boolean)
      .some((value) => value.toString().toLowerCase().includes(input));
  };

  const formatAddressOptionLabel = (option) => (
    <div>
      <div>{option.name_address || option.label}</div>
      <small className="text-muted">
        {option.preview || formatAddressPreview(option) || "Adresse inconnue"}
      </small>
    </div>
  );

  const addressesFormat = useMemo(
    () => addresses.map(buildAddressOption),
    [addresses, buildAddressOption]
  );

  useEffect(() => {
    const selected = addressesFormat.find(
      (item) => item.value === billingInfo.addressBook?.id
    );
    setSelectedOptions(selected || null);
  }, [addressesFormat, billingInfo.addressBook?.id]);

  const customer = {};

  const [customerInfo, setCustomerInfo] = useState({
    disableUnpaidReminder: false,
    type: "normal",
    email: customer?.email || "",
    companyBis: customer?.companyBis?.id || "",
    companyBisName: customer?.companyBis?.name || "",
    contactFirstname: customer?.contactFirstname || "",
    contactLastname: customer?.contactLastname || "",
    contactPhone: customer?.contactPhone || "",
    contactPhoneDirect: customer?.contactPhoneDirect || "",
    firstname: customer?.firstname || "",
    lastname: customer?.lastname || "",
    phone: customer?.phone || "",
    company: customer?.company || "",
    street_number: customer?.street_number || "",
    street: customer?.street || "",
    address_complement: customer?.address_complement || "",
    postal_code: customer?.postal_code || "",
    city: customer?.city || "",
    // Plus d'adresse de livraison à la création d'un client : elle se saisit sur le
    // devis, prestation par prestation (carte Trello WMs93rwA, 30/10/2026).
  });

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const handleChange = (e) => {
    setCustomerInfo({
      ...customerInfo,
      [e.target.name]: e.target.value,
    });
  };

  const handleSelectAddressChange = (event) => {
    setSelectedOptions(event);
    const selectedAddress = addresses.find(
      (adresse) => adresse.id === Number(event.value)
    );
    if (selectedAddress) {
      setCustomerInfo({
        ...customerInfo,
        street_number: selectedAddress?.street_number || "",
        street: selectedAddress?.street || "",
        postal_code: selectedAddress?.postal_code || "",
        city: selectedAddress?.city || "",
        address_complement: selectedAddress?.address_complement || "",
      });
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      await dispatch(addCustomer(customerInfo)).then(async (resp) => {
        setIsLoading(false);
        let msg = "Le client a été ajouté avec succès";
        if (resp?.result?.success === false || resp?.success === false) {
          msg = resp?.result?.message || resp?.message || "Une erreur est survenue";
          swal({
            title: "Attention ",
            text: msg,
            icon: "warning",
            dangerMode: true,
          });
        } else {
          // Si ok, récupère le dernier client dans le listing
          const customer = ATSCustomer(resp);
          if (onCreated) {
            // Mode « rappel » (ex. modale Événement) : on ne pollue pas le
            // brouillon de devis (orderEdition), on remonte juste le client créé.
            dispatch(initialize(true));
            onCreated(customer, customerInfo);
          } else {
            await handleCustomerSelect(customer, customerInfo);
            dispatch(initialize());
          }
          handleClose();
          swal({
            title: "Information",
            text: "Le client a été ajouté avec succès",
            icon: "success",
            dangerMode: false,
          });
        }
      });
    } catch (err) {
      setIsLoading(false);
      swal({
        title: "Attention ",
        text: err?.message || "Une erreur est survenue",
        icon: "warning",
        dangerMode: true,
      });
    }
  };

  // Dispatch également l'entreprise et les infos de facturation
  const handleCustomerSelect = async (customer, customerInfo) => {
    dispatch(setCustomer(customer));
    let company = companies.find(
      (company) => company.id === customer.company_id
    );
    if (company) {
      dispatch(setCompany(company));
    }
    if (customer) {
      const fullAddress =
      customerInfo?.street_number +
        " " +
        customerInfo?.street +
        " " +
        customerInfo?.postal_code +
        " " +
        customerInfo?.city;
      dispatch(
        setBillingInfo({
          ...billingInfo,
          lastname: customerInfo?.lastname,
          firstname: customerInfo?.firstname,
          phone: customerInfo?.phone,
          addressComplement: customerInfo?.address_complement || "",
          fullAddress: fullAddress,
          company: company?.name || customerInfo.company,
          streetNumber: customerInfo?.street_number || "",
          street: customerInfo?.street || "",
          postalCode: customerInfo?.postal_code || "",
          city: customerInfo?.city || "",
        })
      );
    }
  };



  return (
    <>
      <Button variant="success" onClick={handleShow}>
        +
      </Button>

      <Modal show={show} onHide={handleClose} dialogClassName="modal-xl">
        <Modal.Header closeButton>
          <Modal.Title>Ajouter un client</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {isLoading ? 
            <div>Enregistrement en cours...</div>
          :
            <Form onSubmit={handleSubmit}>
              <Row>
                <Col md={6}>
                  <h3>Contact commercial</h3>
                  <Form.Group>
                    <Form.Label>Entreprise*</Form.Label>
                    <InputGroup>
                      <CompanySelectComponent
                        selected={
                          customerInfo.companyBisName
                            ? { name: customerInfo.companyBisName }
                            : null
                        }
                        onSelect={(company) => {
                          setCustomerInfo({
                            ...customerInfo,
                            companyBis: company.id,
                            companyBisName: company.name,
                          });
                        }}
                      />
                      <InputGroup.Append>
                        <AddCompanyComponent
                          onCreated={(company) => {
                            setCustomerInfo({
                              ...customerInfo,
                              companyBis: company?.id || "",
                              companyBisName: company?.name || "",
                            });
                          }}
                        />
                      </InputGroup.Append>
                    </InputGroup>
                  </Form.Group>
                  <Form.Group>
                  <Form.Label>Client d'appel d'offre*</Form.Label>
                    <Form.Control
                      as="select"
                      name="type"
                      value={customerInfo.type}
                      onChange={handleChange}
                      required
                    >
                      <option value="normal">Normal</option>
                      <option value="tender">Appel d'offre</option>
                    </Form.Control>
                  </Form.Group>
                  <Form.Group>
                    <Form.Check 
                      type="checkbox" 
                      label="Désactiver les relances d'impayées ?"
                      name="disableUnpaidReminder"
                      value={customerInfo.disableUnpaidReminder}
                      onChange={handleChange}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Email*</Form.Label>
                    <Form.Control
                      type="email"
                      name="email"
                      value={customerInfo.email}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Prénom du contact*</Form.Label>
                    <Form.Control
                      type="text"
                      name="contactFirstname"
                      value={customerInfo.contactFirstname}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Nom du contact*</Form.Label>
                    <Form.Control
                      type="text"
                      name="contactLastname"
                      value={customerInfo.contactLastname}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Téléphone principal*</Form.Label>
                    <Form.Control
                      type="text"
                      name="contactPhone"
                      value={customerInfo.contactPhone}
                      onChange={handleChange}
                      pattern="^(0[1-9]([\-\. ]?[0-9]{2}){4}|\+[0-9]{1,3}[ \-\.]?[0-9]([ \-\.]?[0-9]){5,12})$"
                      title="Numéro français (ex : 06 12 34 56 78) ou avec indicatif pays (ex : +41 79 123 45 67)"
                      required
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Téléphone secondaire</Form.Label>
                    <Form.Control
                      type="text"
                      name="contactPhoneDirect"
                      value={customerInfo.contactPhoneDirect}
                      onChange={handleChange}
                      pattern="^(0[1-9]([\-\. ]?[0-9]{2}){4}|\+[0-9]{1,3}[ \-\.]?[0-9]([ \-\.]?[0-9]){5,12})$"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <h3>Adresse de facturation</h3>
                  <Form.Group>
                    <Form.Label>Carnet d'adresses</Form.Label>
                    <Select
                      options={addressesFormat}
                      closeMenuOnSelect={true}
                      onChange={handleSelectAddressChange}
                      value={optionSelected}
                      name={"address"}
                formatOptionLabel={formatAddressOptionLabel}
                filterOption={filterAddressOption}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Prénom</Form.Label>
                    <Form.Control
                      type="text"
                      name="firstname"
                      value={customerInfo.firstname}
                      onChange={handleChange}
                      pattern=".{2,25}"
                      title="form.validation.length_2_25"
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Nom</Form.Label>
                    <Form.Control
                      type="text"
                      name="lastname"
                      value={customerInfo.lastname}
                      onChange={handleChange}
                      pattern=".{2,25}"
                      title="form.validation.length_2_25"
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Portable</Form.Label>
                    <Form.Control
                      type="text"
                      name="phone"
                      value={customerInfo.phone}
                      onChange={handleChange}
                      pattern="^(0[1-9]([\-\. ]?[0-9]{2}){4}|\+[0-9]{1,3}[ \-\.]?[0-9]([ \-\.]?[0-9]){5,12})$"
                      title="Numéro français (ex : 06 12 34 56 78) ou avec indicatif pays (ex : +41 79 123 45 67)"
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Recherche d'adresse (remplit les champs ci-dessous)</Form.Label>
                    <AddressAutocompleteInput
                      onSelect={(address) =>
                        setCustomerInfo({
                          ...customerInfo,
                          street_number: address.streetNumber,
                          street: address.street,
                          postal_code: address.postalCode,
                          city: address.city,
                        })
                      }
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Numéro de rue</Form.Label>
                    <Form.Control
                      type="text"
                      name="street_number"
                      value={customerInfo.street_number}
                      onChange={handleChange}
                      pattern=".{1,7}"
                      title="form.validation.length_1_7"
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Rue</Form.Label>
                    <Form.Control
                      type="text"
                      name="street"
                      value={customerInfo.street}
                      onChange={handleChange}
                      pattern=".{2,50}"
                      title="form.validation.length_2_50"
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Complément d'adresse (Bâtiment, code d'accès, interphone, étage)</Form.Label>
                    <Form.Control
                      type="text"
                      name="address_complement"
                      value={customerInfo.address_complement}
                      onChange={handleChange}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Code postal</Form.Label>
                    <Form.Control
                      type="text"
                      name="postal_code"
                      value={customerInfo.postal_code}
                      onChange={handleChange}
                      minLength="5"
                      maxLength="5"
                      title="form.validation.postal_code"
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Ville</Form.Label>
                    <Form.Control
                      type="text"
                      name="city"
                      value={customerInfo.city}
                      onChange={handleChange}
                      pattern=".{2,50}"
                      title="form.validation.length_2_50"
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Button variant="success" type="submit" block size="lg" className="mt-3">
                Enregistrer
              </Button>
            </Form>
          }
        </Modal.Body>
      </Modal>
    </>
  );
};

export default AddCustomerComponent;
